if (!customElements.get('finance-component')) {
  customElements.define(
    'finance-component',
    class FinanceComponent extends HTMLElement {
      constructor() {
        super();
        this.selectedShippingHandle = null;
        this.shippingRates = [];
        this.shippingSubtitles = {};
        this.pricing = { subtotal: 0, shipping: 0, tax: 0, total: 0 };
        this.orderContext = {};
        this.currency = 'USD';
        this.locale = 'en-US';
        this.countryConfig = {};
        this.selectors = {
          modal: '.finance-modal',
          steps: '[data-finance-step]',
          dialog: '.finance-modal__dialog',
          continueButton: '[data-finance-continue]',
          checkoutButton: '[data-finance-checkout]',
          eveMethodButton: '[data-finance-method-eve]',
          checkoutMethodButton: '[data-finance-method-checkout]',
          backButton: '[data-finance-back]',
          form: '[data-finance-form]',
          requiredFields: '[data-finance-required]',
          nameFields: '[data-finance-name]',
          phoneFields: '[data-finance-phone]',
          zipFields: '[data-finance-zip]',
          financeNotification: '[data-finance-notification]',
          financeNotificationMessage: '[data-finance-notification-message]',
          financeNotificationClose: '[data-finance-notification-close]',
          shippingOptions: '[data-shipping-options]',
          shippingSection: '[data-finance-shipping-section]',
          shippingDivider: '[data-finance-shipping-divider]',
          summaryShippingLine: '[data-finance-summary-shipping-line]',
          summaryProducts: '[data-summary-products]',
          summarySubtotal: '[data-summary-subtotal]',
          summaryShipping: '[data-summary-shipping]',
          summaryTax: '[data-summary-tax]',
          summaryTotal: '[data-summary-total]',
          methodPriceBnpl: '[data-finance-method-price="bnpl"]',
          methodPriceTraditional: '[data-finance-method-price="traditional"]',
          methodSavings: '[data-finance-method-savings]',
          methodUpfrontButtonPrice: '[data-finance-method-upfront-button]',
          upfrontTitleSavings: '[data-finance-upfront-title-savings]',
          upfrontTitlePlain: '[data-finance-upfront-title-plain]',
          close: '[data-finance-close]',
          open: '[data-finance-open]',
          shippingOption: '[data-shipping-option]',
          countriesScript: '[data-finance-countries]',
          shippingSubtitlesScript: '[data-finance-shipping-subtitles]',
          messagesScript: '[data-finance-messages]',
          statesScript: '[data-finance-states]',
          checkoutUrlScript: '[data-finance-checkout-url]',
          inlineTerms: '[data-finance-inline-terms]',
          orderScript: '[data-finance-order]',
          scrollArea: '.finance-step__scroll',
          scrollBody: '.finance-step__body',
          scrollFadeTop: '.finance-step__scroll-fade--top',
          scrollFadeBottom: '.finance-step__scroll-fade--bottom',
          loadingSpinner: '.loading__spinner',
          step1: '[data-finance-step="1"]',
          step2: '[data-finance-step="2"]',
          progressComplete: '[data-finance-progress-complete]',
          progressBar: '[data-finance-progress-bar]',
          progressFill: '[data-finance-progress-fill]',
          attributes: {
            financeStep: 'data-finance-step',
            shippingOption: 'data-shipping-option',
            orderTarget: 'data-finance-order-target',
            hidden: 'hidden',
          },
        };

        this.onContinueClick = this.onContinueClick.bind(this);
        this.onCheckoutClick = this.onCheckoutClick.bind(this);
        this.onEveMethodClick = this.onEveMethodClick.bind(this);
        this.onCheckoutMethodClick = this.onCheckoutMethodClick.bind(this);
        this.onBackClick = this.onBackClick.bind(this);
        this.onSummaryProductsClick = this.onSummaryProductsClick.bind(this);
        this.close = this.close.bind(this);

        this._currentStep = 0;
        this._openTrigger = null;
        this._isClosing = false;
        this._cachedQuotePayloadKey = null;
        this._quoteDataReady = false;
        this.unavailableItems = [];
        this.shippingUnavailableMessage = '';
        this.blockedStates = [];
        this.messages = {};
        this.financeRequestTimeoutMs = 15000;
        this._financeAbortController = null;
        this._financeTimeoutId = null;
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this._cartUpdateUnsubscriber?.();
        this._cartUpdateUnsubscriber = null;
        this.stopOrderScriptWatch();
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;

        const s = this.selectors;

        this.modal = this.querySelector(s.modal);
        this.steps = this.querySelectorAll(s.steps);
        this.dialog = this.querySelector(s.dialog);
        this.continueButton = this.querySelector(s.continueButton);
        this.checkoutButton = this.querySelector(s.checkoutButton);
        this.eveMethodButton = this.querySelector(s.eveMethodButton);
        this.checkoutMethodButton = this.querySelector(s.checkoutMethodButton);
        this.form = this.querySelector(s.form);
        this.requiredFields = this.querySelectorAll(s.requiredFields);
        this.nameFields = this.querySelectorAll(s.nameFields);
        this.phoneFields = this.querySelectorAll(s.phoneFields);
        this.zipFields = this.querySelectorAll(s.zipFields);
        this.shippingOptionsContainer = this.querySelector(s.shippingOptions);
        this.shippingSection = this.querySelector(s.shippingSection);
        this.shippingDivider = this.querySelector(s.shippingDivider);
        this.summaryShippingLine = this.querySelector(s.summaryShippingLine);
        this.summaryProductsContainer = this.querySelector(s.summaryProducts);
        this.summarySubtotal = this.querySelector(s.summarySubtotal);
        this.summaryShipping = this.querySelector(s.summaryShipping);
        this.summaryTax = this.querySelector(s.summaryTax);
        this.summaryTotal = this.querySelector(s.summaryTotal);
        this.methodPriceBnpl = this.querySelector(s.methodPriceBnpl);
        this.methodPriceTraditional = this.querySelector(s.methodPriceTraditional);
        this.methodSavings = this.querySelector(s.methodSavings);
        this.methodUpfrontButtonPrice = this.querySelector(s.methodUpfrontButtonPrice);
        this.upfrontTitleSavings = this.querySelector(s.upfrontTitleSavings);
        this.upfrontTitlePlain = this.querySelector(s.upfrontTitlePlain);

        this.removeItemLabel = this.dataset.financeRemoveLabel || 'Remove';
        this.unavailableItemLabel = this.dataset.financeUnavailableLabel || 'Unavailable for delivery';
        this.removeIconHtml =
          this.querySelector('[data-finance-remove-icon]')?.innerHTML.trim() || '';

        this.loadConfig();
        this.populateStates();
        this.bindEvents();
        this.initScrollFades();
        this.updateContinueState();
        this.updateCheckoutMethodState();
        this.updateEveMethodState();
        this.updateCheckoutState();
      }

      loadConfig() {
        const s = this.selectors;
        this.countryConfig = this.parseJsonScript(s.countriesScript)?.countries || {};
        this.shippingSubtitles = this.parseJsonScript(s.shippingSubtitlesScript) || {};
        this.messages = this.parseJsonScript(s.messagesScript) || {};
        this.checkoutUrlScript = this.querySelector(s.checkoutUrlScript);
        this.blockedStates = this.parseBlockedStates();
      }

      message(key, fallback) {
        const value = this.messages?.[key];
        return typeof value === 'string' && value.trim() ? value : fallback;
      }

      parseBlockedStates() {
        const raw = this.dataset.financeBlockedStates || 'GA';

        return raw
          .split(/[\s,]+/)
          .map((code) => code.trim().toUpperCase())
          .filter(Boolean);
      }

      isZipLibraryAvailable() {
        return typeof window.zipState === 'function';
      }

      getZipStateFromLibrary(zip5) {
        if (!this.isZipLibraryAvailable() || !zip5) return null;

        return window.zipState(String(zip5).slice(0, 5));
      }

      warnZipLibraryOnce() {
        if (this._warnedZipLibrary) return;
        this._warnedZipLibrary = true;
        this.logFinance('ZIP state library unavailable — skipping ZIP-to-state match');
      }

      getSelectedStateCode() {
        return (this.stateSelect || this.form?.state)?.value || '';
      }

      getStateLabelForCode(code) {
        const stateSelect = this.stateSelect || this.form?.querySelector('#FinanceState');
        const match = [...(stateSelect?.options || [])].find((option) => option.value === code);

        return match?.textContent?.trim() || code || this.message('thisState', 'this state');
      }

      isBlockedState(code) {
        if (!code) return false;

        return this.blockedStates.includes(String(code).trim().toUpperCase());
      }

      decodeHtmlEntities(value) {
        if (!value) return '';

        const el = document.createElement('textarea');
        el.innerHTML = value;
        return el.value;
      }

      getBlockedStateMessage(code) {
        const template = this.decodeHtmlEntities(
          this.dataset.financeBlockedStateTemplate || "We are unable to offer Eve financing in __STATE__."
        );

        return template.replace('__STATE__', this.getStateLabelForCode(code));
      }

      getBlockedAddressError() {
        const state = this.getSelectedStateCode();

        if (this.isBlockedState(state)) {
          return this.getBlockedStateMessage(state);
        }

        const zip5 = this.getZip5(this.zipFields?.[0]?.value);

        if (zip5 && this.isZipLibraryAvailable()) {
          const derivedState = this.getZipStateFromLibrary(zip5);
          if (this.isBlockedState(derivedState)) {
            return this.getBlockedStateMessage(derivedState);
          }
        }

        return null;
      }

      populateStates() {
        const states = this.parseJsonScript(this.selectors.statesScript);
        const stateSelect = this.form?.querySelector('#FinanceState');

        if (!stateSelect || !Array.isArray(states)) return;

        states.forEach(({ value, label }) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          stateSelect.appendChild(option);
        });
      }

      parseJsonScript(selector) {
        const el = this.querySelector(selector) || document.querySelector(selector);
        return this.parseJsonElement(el);
      }

      parseJsonScriptFrom(parent, selector) {
        if (!parent) return null;
        return this.parseJsonElement(parent.querySelector(selector));
      }

      getOrderDataFromTrigger(trigger) {
        const targetId = trigger.getAttribute(this.selectors.attributes.orderTarget);
        if (!targetId) {
          this.logFinance('Order script missing — no data-finance-order-target on button');
          return null;
        }

        const scopedEl =
          trigger.closest('.finance-button-wrap')?.querySelector('[data-finance-order]') ||
          trigger.closest('[id$="-FinanceButton"]')?.querySelector('[data-finance-order]');
        const el = scopedEl || document.getElementById(targetId);
        if (!el) {
          this.logFinance('Order script not found — id:', targetId);
          return null;
        }

        const data = this.parseJsonElement(el);
        if (!data) {
          this.logFinance('Order script JSON parse failed — id:', targetId);
        }

        return data;
      }

      normalizeCartTotal(value) {
        const total = Number(value);
        return Number.isFinite(total) && total >= 0 ? total : null;
      }

      fetchLiveCartTotal() {
        const cartUrl = window.routes?.cart_url || '/cart';

        return fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error('cart.js');
            return response.json();
          })
          .then((cart) => this.normalizeCartTotal(cart?.total_price))
          .catch((error) => {
            this.logFinance('Live cart total fetch failed', error);
            return null;
          });
      }

      syncLiveCartTotal() {
        if (this.orderContext?.type !== 'cart') return;

        this.fetchLiveCartTotal().then((liveTotal) => {
          if (liveTotal === null || this.orderContext?.type !== 'cart') return;
          if (this.orderContext.cartTotal === liveTotal) return;

          this.orderContext.cartTotal = liveTotal;
          this.updateMethodCardPrices();
        });
      }

      getSelectedVariantFromTrigger(trigger) {
        const selectedVariantEl = trigger
          .closest('product-info')
          ?.querySelector('[data-selected-variant]');

        if (!selectedVariantEl) return null;

        try {
          return JSON.parse(selectedVariantEl.textContent);
        } catch {
          return null;
        }
      }

      buildOrderItems(orderData, trigger) {
        if (!orderData) return [];

        if (orderData.type === 'cart') {
          return Array.isArray(orderData.items) ? orderData.items.slice() : [];
        }

        if (orderData.type === 'product' && orderData.product) {
          const items = [orderData.product, ...(orderData.addons || [])];
          const selectedVariant = this.getSelectedVariantFromTrigger(trigger);
          const variantInput =
            trigger.closest('product-form')?.querySelector('input[name="id"]') ||
            trigger.closest('form')?.querySelector('input[name="id"]');
          const selectedVariantId =
            selectedVariant?.id || variantInput?.value || orderData.product.variantId;

          if (selectedVariant?.id) {
            const tags = Array.isArray(selectedVariant.options)
              ? selectedVariant.options.filter((option) => option && option !== 'Default Title')
              : items[0].tags;
            const image = selectedVariant.featured_image?.src || items[0].image;

            items[0] = {
              ...items[0],
              variantId: selectedVariant.id,
              price: selectedVariant.price ?? items[0].price,
              image,
              tags,
            };
          } else if (selectedVariantId) {
            items[0] = {
              ...items[0],
              variantId: selectedVariantId,
            };
          }

          return items;
        }

        return [];
      }

      parseJsonElement(el) {
        if (!el) return null;

        try {
          return JSON.parse(el.textContent);
        } catch {
          return null;
        }
      }

      writeJsonScript(el, data) {
        if (!el) return;
        el.textContent = JSON.stringify(data);
      }

      getCountryConfig(code) {
        return this.countryConfig[code] || null;
      }

      logFinance(label, data) {
        if (typeof data === 'undefined') {
          console.log('[Finance]', label);
          return;
        }

        console.log('[Finance]', label, data);
      }

      bindEvents() {
        const s = this.selectors;

        document.addEventListener('click', (event) => {
          const trigger = event.target.closest(s.open);
          if (!trigger) return;
          event.preventDefault();
          this.openFromTrigger(trigger);
        });

        this.modal?.querySelectorAll(s.close).forEach((el) => {
          el.addEventListener('click', this.close);
        });

        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          if (this.isOpen()) {
            this.close();
          }
        });

        this.requiredFields.forEach((field) => {
          field.addEventListener('input', () => {
            this.clearFieldError(field);
            this.updateContinueState();
          });
          field.addEventListener('change', () => {
            if (field.matches('select') && field.value) {
              field.classList.add('finance-field__select--filled');
            }
            this.clearFieldError(field);
            this.updateContinueState();
          });
          field.addEventListener('focusout', () => {
            this.normalizeAutofillField(field);
            this.updateContinueState();
          });
          field.addEventListener('blur', () => {
            this.normalizeAutofillField(field);
            this.updateContinueState();
          });
          field.addEventListener('animationstart', (event) => {
            if (event.animationName !== 'finance-autofill') return;
            this.normalizeAutofillField(field);
            this.updateContinueState();
          });
        });

        this.nameFields.forEach((field) => {
          field.addEventListener('input', () => this.handleNameInput(field));
          field.addEventListener('paste', (event) => this.handleNamePaste(field, event));
        });

        this.phoneFields.forEach((field) => {
          field.addEventListener('input', () => this.handlePhoneInput(field));
          field.addEventListener('paste', (event) => this.handlePhonePaste(field, event));
        });

        this.zipFields.forEach((field) => {
          field.addEventListener('input', () => this.handleZipInput(field));
          field.addEventListener('paste', (event) => this.handleZipPaste(field, event));
        });

        this.stateSelect = this.form?.querySelector('#FinanceState');
        this.stateSelect?.addEventListener('change', () => {
          this.zipFields.forEach((field) => this.clearFieldError(field));
          this.validateStateField();
          this.updateContinueState();
        });

        this.continueButton?.addEventListener('click', this.onContinueClick);
        this.checkoutButton?.addEventListener('click', this.onCheckoutClick);
        this.eveMethodButton?.addEventListener('click', this.onEveMethodClick);
        this.checkoutMethodButton?.addEventListener('click', this.onCheckoutMethodClick);
        this.querySelectorAll(s.inlineTerms).forEach((checkbox) => {
          checkbox.addEventListener('change', () => this.onInlineTermsChange());
        });
        this.querySelectorAll(s.backButton).forEach((button) => {
          button.addEventListener('click', this.onBackClick);
        });

        this.querySelectorAll(s.financeNotificationClose).forEach((button) => {
          button.addEventListener('click', () => this.clearFinanceNotification());
        });

        this.summaryProductsContainer?.addEventListener('click', this.onSummaryProductsClick);
        this.bindCartOrderRefresh();
      }

      bindCartOrderRefresh() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (!this.isOpen() || this.orderContext?.type !== 'cart') return;
          this.watchOrderScriptRefresh();
        });
      }

      stopOrderScriptWatch() {
        this._orderScriptObserver?.disconnect();
        this._orderScriptObserver = null;
        if (this._orderScriptWatchTimer) {
          window.clearTimeout(this._orderScriptWatchTimer);
          this._orderScriptWatchTimer = null;
        }
      }

      watchOrderScriptRefresh() {
        const targetId = this._openTrigger?.getAttribute(this.selectors.attributes.orderTarget);
        if (!targetId) return;

        const currentText = () => document.getElementById(targetId)?.textContent ?? null;
        const previous = currentText();
        this.stopOrderScriptWatch();

        const applyIfChanged = () => {
          const next = currentText();
          if (next == null || next === previous) return false;
          this.applyLiveCartOrder(this.parseJsonElement(document.getElementById(targetId)));
          return true;
        };

        if (applyIfChanged()) return;
        const observer = new MutationObserver(() => {
          if (!applyIfChanged()) return;
          this.stopOrderScriptWatch();
        });
        this._orderScriptObserver = observer;
        observer.observe(document.body, { childList: true, subtree: true, characterData: true });
        this._orderScriptWatchTimer = window.setTimeout(() => {
          if (this._orderScriptObserver !== observer) return;
          this.stopOrderScriptWatch();
        }, 4000);
      }

      applyLiveCartOrder(orderData) {
        if (!this.isOpen() || !orderData || orderData.type !== 'cart') return;

        const items = Array.isArray(orderData.items) ? orderData.items.slice() : [];
        const previousKey = this.getQuotePayloadKey();

        this.orderContext.items = items;
        this.orderContext.cartTotal = this.normalizeCartTotal(orderData.cartTotal);
        this.orderContext.discounts = Array.isArray(orderData.discounts) ? orderData.discounts : [];

        if (previousKey !== this.getQuotePayloadKey()) {
          this._quoteDataReady = false;
          this._cachedQuotePayloadKey = null;
          this.unavailableItems = [];
          this.shippingRates = [];
          this.selectedShippingHandle = null;
          this.pricing = { subtotal: 0, shipping: 0, tax: 0, total: 0 };
          this.toggleShippingSection(false);
          this.resetSummaryDisplay();
          this.hideShippingUnavailableMessage();
          this.updateCheckoutState();
        }

        this.renderSummaryProducts(this.orderContext.items);
        this.updateMethodCardPrices();
        if (this._quoteDataReady) {
          const rate = this.getSelectedShippingRate();
          if (rate) this.applySelectedRate(rate);
          this.updateSummaryDisplay();
        }
        requestAnimationFrame(() => this.updateScrollFades());
      }

      onSummaryProductsClick(event) {
        const removeButton = event.target.closest('[data-finance-remove-item]');
        if (!removeButton) return;

        event.preventDefault();
        this.removeOrderItem(removeButton.getAttribute('data-finance-remove-item'));
      }

      shouldShowEveOption() {
        return this.dataset.financeShowEve !== 'false';
      }

      shouldShowShopPayOption() {
        return this.dataset.financeShowShopPay !== 'false';
      }

      getInitialStepNumber() {
        if (this.shouldShowEveOption() && !this.shouldShowShopPayOption()) {
          return 1;
        }

        return 0;
      }

      onEveMethodClick() {
        if (this.eveMethodButton?.classList.contains('loading')) return;

        this.clearFinanceError();
        if (!this.requireTermsAccepted('eve-method')) return;
        this.showStep(1);
      }

      onCheckoutMethodClick() {
        if (this.checkoutMethodButton?.classList.contains('loading')) return;

        this.clearFinanceError();
        this.goToStandardCheckout();
      }

      getShopifyRootPath() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      getProductCheckoutQuantity() {
        const quantityInput =
          this._openTrigger?.closest('product-form')?.querySelector('[name="quantity"]') ||
          this._openTrigger?.closest('product-info')?.querySelector('.quantity__input');
        const fromInput = parseInt(quantityInput?.value || '', 10);
        if (Number.isFinite(fromInput) && fromInput > 0) return fromInput;

        const fromOrder = parseInt(this.orderContext?.items?.[0]?.quantity || '', 10);
        if (Number.isFinite(fromOrder) && fromOrder > 0) return fromOrder;

        return 1;
      }

      getProductCheckoutVariantId() {
        const selectedVariant = this._openTrigger
          ? this.getSelectedVariantFromTrigger(this._openTrigger)
          : null;
        if (selectedVariant?.id) return selectedVariant.id;

        const variantInput =
          this._openTrigger?.closest('product-form')?.querySelector('input[name="id"]') ||
          this._openTrigger?.closest('form')?.querySelector('input[name="id"]');
        if (variantInput?.value) return variantInput.value;

        return this.orderContext?.items?.[0]?.variantId || null;
      }

      setCheckoutMethodLoading(isLoading) {
        if (!this.checkoutMethodButton) return;

        this.checkoutMethodButton.classList.toggle('loading', isLoading);
        this.checkoutMethodButton
          .querySelector(this.selectors.loadingSpinner)
          ?.classList.toggle('hidden', !isLoading);
        this.updateCheckoutMethodState();
      }

      goToStandardCheckout() {
        const root = this.getShopifyRootPath();
        const orderType = this.orderContext?.type || 'product';

        if (orderType === 'cart') {
          this.setCheckoutMethodLoading(true);
          window.location.href = `${root}checkout`;
          return;
        }

        const variantId = this.getProductCheckoutVariantId();
        if (!variantId) {
          this.logFinance('Standard checkout aborted — missing variant id');
          return;
        }

        const quantity = this.getProductCheckoutQuantity();
        this.setCheckoutMethodLoading(true);
        window.location.href = `${root}cart/${variantId}:${quantity}?checkout`;
      }

      isFromCart() {
        return this.orderContext?.type === 'cart';
      }

      applyTermsVisibility() {
        this.classList.toggle('finance-component--from-cart', this.isFromCart());
        this.querySelectorAll(this.selectors.inlineTerms).forEach((checkbox) => {
          checkbox.checked = false;
        });
        this.applyEveFormTermsVisibility();
        this.updateCheckoutMethodState();
        this.updateEveMethodState();
        this.updateContinueState();
      }

      shouldSkipEveFormTerms() {
        if (this.isFromCart()) return true;
        const methodTerms = this.getTermsCheckbox('eve-method');
        return Boolean(methodTerms && methodTerms.checked);
      }

      applyEveFormTermsVisibility() {
        this.classList.toggle('finance-component--skip-eve-terms', this.shouldSkipEveFormTerms());
      }

      getTermsCheckbox(placement) {
        return this.querySelector(`[data-finance-inline-terms="${placement}"]`);
      }

      hasCheckoutTermsAccepted() {
        if (this.isFromCart()) return true;
        const checkbox = this.getTermsCheckbox('checkout');
        return !checkbox || checkbox.checked;
      }

      hasEveTermsAccepted() {
        if (this.shouldSkipEveFormTerms()) return true;
        const checkbox = this.getTermsCheckbox('eve');
        return !checkbox || checkbox.checked;
      }

      hasEveMethodTermsAccepted() {
        if (this.isFromCart()) return true;
        const checkbox = this.getTermsCheckbox('eve-method');
        return !checkbox || checkbox.checked;
      }

      requireTermsAccepted(placement) {
        let accepted = this.hasEveTermsAccepted();

        if (placement === 'checkout') {
          accepted = this.hasCheckoutTermsAccepted();
        } else if (placement === 'eve-method') {
          accepted = this.hasEveMethodTermsAccepted();
        }

        if (accepted) return true;

        this.showFinanceNotification(this.message('terms', 'Please agree to the Terms & Conditions before continuing.'));
        return false;
      }

      onInlineTermsChange() {
        this.clearFinanceError();
        this.applyEveFormTermsVisibility();
        this.updateCheckoutMethodState();
        this.updateEveMethodState();
        this.updateContinueState();
      }

      updateCheckoutMethodState() {
        if (!this.checkoutMethodButton) return;

        const isLoading = this.checkoutMethodButton.classList.contains('loading');
        this.checkoutMethodButton.disabled = isLoading;
      }

      updateEveMethodState() {
        if (!this.eveMethodButton) return;

        const isLoading = this.eveMethodButton.classList.contains('loading');
        this.eveMethodButton.disabled = isLoading || !this.hasEveMethodTermsAccepted();
      }

      openFromTrigger(trigger) {
        if (trigger.hasAttribute('disabled')) return;

        this._openTrigger = trigger;
        const targetId = trigger.getAttribute(this.selectors.attributes.orderTarget);
        const orderData = this.getOrderDataFromTrigger(trigger) || {};
        const items = this.buildOrderItems(orderData, trigger);

        this.logFinance('Modal opened — order script id:', targetId);
        this.logFinance('Modal opened — raw order data:', orderData);
        this.logFinance('Modal opened — merged items for summary/API:', items);

        this.orderContext = {
          type: orderData.type || 'product',
          items,
          cartTotal: this.normalizeCartTotal(orderData.cartTotal),
          discounts: Array.isArray(orderData.discounts) ? orderData.discounts : [],
        };

        this.selectedShippingHandle = null;
        this.shippingRates = [];
        this.pricing = { subtotal: 0, shipping: 0, tax: 0, total: 0 };
        this._cachedQuotePayloadKey = null;
        this._quoteDataReady = false;
        this.unavailableItems = [];
        this.shippingUnavailableMessage = '';

        this.hideShippingUnavailableMessage();
        this.setCheckoutUrl('');
        this.clearFinanceError();

        this.toggleShippingSection(false);

        this.renderSummaryProducts(this.orderContext.items);
        this.resetSummaryDisplay();
        this.updateMethodCardPrices();
        this.syncLiveCartTotal();
        this.form?.reset();
        this.clearAllFieldErrors();
        this.applyTermsVisibility();
        this._currentStep = this.getInitialStepNumber();
        this.showStep(this._currentStep);
        this.modal?.classList.remove('is-closing');
        this.modal?.removeAttribute(this.selectors.attributes.hidden);
        requestAnimationFrame(() => {
          this.modal?.classList.add('is-open');
        });
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => this.updateScrollFades());
      }

      close() {
        if (!this.modal || this.modal.hasAttribute(this.selectors.attributes.hidden) || this._isClosing) {
          return;
        }

        const finishClose = () => {
          this.modal?.classList.remove('is-open', 'is-closing');
          this.modal?.setAttribute(this.selectors.attributes.hidden, '');
          this._isClosing = false;
          this._currentStep = 0;
          this._openTrigger = null;
          this.stopOrderScriptWatch();
          document.body.style.overflow = '';
          this.resetStepsAfterClose();
          this.setContinueLoading(false);
          this.setCheckoutLoading(false);
          this.setCheckoutMethodLoading(false);
          this.clearFinanceError();
        };

        this.abortFinanceRequest();

        if (this.prefersReducedMotion()) {
          finishClose();
          return;
        }

        this._isClosing = true;
        this.modal.classList.remove('is-open');
        this.modal.classList.add('is-closing');

        const dialog = this.modal.querySelector('.finance-modal__dialog');
        const timeoutId = window.setTimeout(finishClose, 450);

        dialog?.addEventListener(
          'animationend',
          (event) => {
            if (event.target !== dialog) return;
            window.clearTimeout(timeoutId);
            finishClose();
          },
          { once: true }
        );
      }

      resetStepsAfterClose() {
        const hidden = this.selectors.attributes.hidden;
        const stepAttr = this.selectors.attributes.financeStep;

        this.steps?.forEach((step) => {
          const stepNum = parseInt(step.getAttribute(stepAttr), 10);
          step.toggleAttribute(hidden, stepNum !== 0);
        });

        this.dialog?.classList.remove('finance-modal__dialog--loading-step', 'finance-modal__dialog--method-step');
      }

      isOpen() {
        const hidden = this.selectors.attributes.hidden;
        return this.modal && !this.modal.hasAttribute(hidden);
      }

      showStep(stepNumber) {
        if (!this.steps?.length) return;

        const hidden = this.selectors.attributes.hidden;
        const stepAttr = this.selectors.attributes.financeStep;

        if (stepNumber === 2) this.primeStep2Progress();

        this.steps.forEach((step) => {
          const stepNum = parseInt(step.getAttribute(stepAttr), 10);
          step.toggleAttribute(hidden, stepNum !== stepNumber);
        });

        this.dialog?.classList.toggle('finance-modal__dialog--loading-step', stepNumber === 3);
        this.dialog?.classList.toggle('finance-modal__dialog--method-step', stepNumber === 0);

        this._currentStep = stepNumber;
        this.applyEveFormTermsVisibility();
        if (stepNumber === 1) {
          this.updateContinueState();
        }
        if (stepNumber === 2) {
          requestAnimationFrame(() => this.updateStep2Progress());
        }
        requestAnimationFrame(() => this.updateScrollFades());
      }

      initScrollFades() {
        const s = this.selectors;
        this._scrollFadeUpdaters = [];

        this.querySelectorAll(s.scrollArea).forEach((scrollArea) => {
          const body = scrollArea.querySelector(s.scrollBody);
          const fadeTop = scrollArea.querySelector(s.scrollFadeTop);
          const fadeBottom = scrollArea.querySelector(s.scrollFadeBottom);
          if (!body || !fadeTop || !fadeBottom) return;

          const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = body;
            const canScroll = scrollHeight > clientHeight + 1;
            const atTop = scrollTop <= 1;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

            fadeTop.classList.toggle('is-visible', canScroll && !atTop);
            fadeBottom.classList.toggle('is-visible', canScroll && !atBottom);
          };

          body.addEventListener('scroll', update, { passive: true });

          if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(update);
            observer.observe(body);
          }

          this._scrollFadeUpdaters.push(update);
          update();
        });
      }

      updateScrollFades() {
        this._scrollFadeUpdaters?.forEach((update) => update());
      }

      updateContinueState() {
        if (!this.continueButton) return;

        const isLoading = this.continueButton.classList.contains('loading');
        this.continueButton.disabled = isLoading;
        this.updateStep1Progress();
      }

      normalizeAutofillField(field) {
        if (!field) return;

        if (field.type === 'email') {
          field.value = field.value.trim();
          return;
        }

        if (field.matches('[data-finance-name]')) {
          field.value = field.value.replace(/\./g, '');
          return;
        }

        if (field.matches('[data-finance-phone]')) {
          this.applyPhoneValue(field, field.value);
          return;
        }

        if (field.matches('[data-finance-zip]')) {
          this.applyZipValue(field, field.value);
        }
      }

      isRequiredFieldSatisfied(field) {
        if (field.matches('[data-finance-name]')) {
          return Boolean(field.value.trim() && !this.containsPeriod(field.value));
        }

        if (field.matches('[data-finance-phone]')) {
          return this.isValidPhone(field.value);
        }

        if (field.matches('[data-finance-zip]')) {
          return this.isValidZip(field.value);
        }

        if (field.matches('select')) {
          if (field.name === 'state' || field.id === 'FinanceState') {
            return field.value !== '' && !this.isBlockedState(field.value);
          }

          return field.value !== '';
        }

        if (field.type === 'email') {
          const email = field.value.trim();
          if (email !== field.value) field.value = email;
          return email !== '' && field.checkValidity();
        }

        return field.value.trim() !== '';
      }

      getStep1ProgressPercent() {
        const fields = [...this.requiredFields];
        if (!fields.length) return 0;

        const filled = fields.filter((field) => this.isRequiredFieldSatisfied(field)).length;
        return Math.round((filled / fields.length) * 50);
      }

      updateStep1Progress() {
        const header = this.querySelector(`${this.selectors.step1} .finance-step__header`);
        if (!header) return;

        const percent = this.getStep1ProgressPercent();
        if (this._step1ProgressPercent === percent) return;
        this._step1ProgressPercent = percent;

        const completeEl = header.querySelector(this.selectors.progressComplete);
        const bar = header.querySelector(this.selectors.progressBar);
        const fill = header.querySelector(this.selectors.progressFill);
        const template = completeEl?.dataset.financeProgressTemplate || '__PERCENT__% Complete';
        const label = template.replace('__PERCENT__', String(percent));

        if (completeEl) completeEl.textContent = label;
        if (bar) {
          bar.setAttribute('aria-valuenow', String(percent));
          bar.setAttribute('aria-label', label);
        }
        if (fill) fill.style.setProperty('--finance-progress', String(percent / 100));
      }

      getStep2Header() {
        return this.querySelector(`${this.selectors.step2} .finance-step__header`);
      }

      applyStepProgress(header, percent, { immediate = false } = {}) {
        if (!header) return;

        const completeEl = header.querySelector(this.selectors.progressComplete);
        const bar = header.querySelector(this.selectors.progressBar);
        const fill = header.querySelector(this.selectors.progressFill);
        const template = completeEl?.dataset.financeProgressTemplate || '__PERCENT__% Complete';
        const label = template.replace('__PERCENT__', String(percent));

        if (completeEl) completeEl.textContent = label;
        if (bar) {
          bar.setAttribute('aria-valuenow', String(percent));
          bar.setAttribute('aria-label', label);
        }
        if (!fill) return;

        if (immediate) {
          fill.style.transition = 'none';
          fill.style.setProperty('--finance-progress', String(percent / 100));
          void fill.offsetWidth;
          fill.style.transition = '';
          return;
        }

        fill.style.setProperty('--finance-progress', String(percent / 100));
      }

      primeStep2Progress() {
        this.applyStepProgress(this.getStep2Header(), 50, { immediate: true });
      }

      updateStep2Progress() {
        this.applyStepProgress(this.getStep2Header(), 90);
      }

      isStep1FormValid() {
        return [...this.requiredFields].every((field) => this.isRequiredFieldSatisfied(field));
      }

      syncFinanceNotification() {
        if (this._currentStep === 1 && this.isStep1FormValid()) {
          this.clearFinanceNotification();
        }
      }

      validateStep1Form() {
        let isValid = true;

        this.nameFields.forEach((field) => {
          if (!this.validateNameField(field)) isValid = false;
        });

        this.phoneFields.forEach((field) => {
          if (!this.validatePhoneField(field)) isValid = false;
        });

        if (!this.validateStateField()) isValid = false;

        this.zipFields.forEach((field) => {
          if (!this.validateZipField(field)) isValid = false;
        });

        this.requiredFields.forEach((field) => {
          if (field.matches('[data-finance-name], [data-finance-phone], [data-finance-zip], #FinanceState')) {
            return;
          }

          if (field.type === 'email') {
            field.value = field.value.trim();
          }

          if (!field.checkValidity()) {
            isValid = false;
            const message = field.value.trim()
              ? this.getInvalidFieldMessage(field)
              : this.getEmptyFieldMessage(field);
            this.setFieldError(field, message);
          } else {
            this.clearFieldError(field);
          }
        });

        if (!isValid) {
          this.showFirstFieldError();
        } else {
          this.clearFinanceNotification();
        }

        return isValid;
      }

      getEmptyFieldMessage(field) {
        const messages = {
          FinanceFirstName: this.message('firstName', 'Please enter your first name.'),
          FinanceLastName: this.message('lastName', 'Please enter your last name.'),
          FinanceEmail: this.message('email', 'Please enter your email.'),
          FinancePhone: this.message('phone', 'Please enter your phone number.'),
          FinanceAddress1: this.message('address', 'Please enter your address.'),
          FinanceCity: this.message('city', 'Please enter your city.'),
          FinanceState: this.message('state', 'Please select a state.'),
          FinanceZip: this.message('zip', 'Please enter your ZIP code.'),
          FinanceCountry: this.message('country', 'Please select your country.'),
        };

        return messages[field?.id] || this.message('field', 'Please fill out this field.');
      }

      getInvalidFieldMessage(field) {
        if (field?.type === 'email') return this.message('emailInvalid', 'Please enter a valid email.');
        return this.getEmptyFieldMessage(field);
      }

      getNameFieldErrorMessage() {
        return this.message('namePeriod', 'Periods are not allowed in names.');
      }

      containsPeriod(value) {
        return /\./.test(value);
      }

      getPhoneFieldErrorMessage() {
        return this.message('phoneInvalid', 'Enter a valid 10-digit US phone number.');
      }

      getPhoneInvalidCharacterMessage() {
        return this.message('phoneCharacters', 'Use numbers only — no country code or + sign.');
      }

      sanitizePhoneValue(value) {
        return value.replace(/[^0-9()\-\s]/g, '');
      }

      normalizePhoneDigits(value) {
        let digits = String(value).replace(/\D/g, '');

        if (digits.length === 11 && digits.startsWith('1')) {
          digits = digits.slice(1);
        }

        return digits.slice(0, 10);
      }

      formatPhoneDigits(digits) {
        if (!digits) return '';

        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }

      getPhoneDigitCount(value) {
        return this.normalizePhoneDigits(value).length;
      }

      isValidPhone(value) {
        const digits = this.normalizePhoneDigits(value);

        if (digits.length !== 10) return false;

        return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
      }

      applyPhoneValue(field, rawValue) {
        const sanitized = this.sanitizePhoneValue(rawValue);
        const hadInvalidCharacters = sanitized !== rawValue;
        const digits = this.normalizePhoneDigits(sanitized);

        field.value = this.formatPhoneDigits(digits);

        return { digits, hadInvalidCharacters };
      }

      handlePhoneInput(field) {
        this.applyPhoneValue(field, field.value);
        this.clearFieldError(field);
        this.updateContinueState();
      }

      handlePhonePaste(field, event) {
        event.preventDefault();

        const pastedText = event.clipboardData?.getData('text') || '';
        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? field.value.length;
        const merged = `${field.value.slice(0, start)}${pastedText}${field.value.slice(end)}`;
        this.applyPhoneValue(field, merged);
        this.clearFieldError(field);
        this.updateContinueState();
      }

      validatePhoneField(field) {
        this.applyPhoneValue(field, field.value);

        if (!field.value.trim()) {
          this.setFieldError(field, this.getEmptyFieldMessage(field));
          return false;
        }

        if (!this.isValidPhone(field.value)) {
          this.setFieldError(field, this.getPhoneFieldErrorMessage());
          return false;
        }

        this.clearFieldError(field);
        return true;
      }

      getZipFieldErrorMessage() {
        return this.message('zipInvalid', 'Enter a valid US ZIP code (12345 or 12345-6789).');
      }

      getZipNotFoundMessage() {
        return this.message('zipNotFound', 'This ZIP code is not a valid US ZIP code.');
      }

      getSelectedStateLabel() {
        const stateSelect = this.stateSelect || this.form?.querySelector('#FinanceState');

        if (!stateSelect?.value) return this.message('selectedState', 'the selected state');

        return stateSelect.options[stateSelect.selectedIndex]?.textContent?.trim() || stateSelect.value;
      }

      getZipStateMismatchMessage() {
        const template = this.message('zipMismatch', 'This ZIP code is not valid for __STATE__.');
        return template.replace('__STATE__', this.getSelectedStateLabel());
      }

      getZipStateRequiredMessage() {
        return this.message('zipStateRequired', 'Select a state to validate your ZIP code.');
      }

      getZipInvalidCharacterMessage() {
        return this.message('zipCharacters', 'ZIP code can only contain numbers.');
      }

      sanitizeZipValue(value) {
        return value.replace(/[^0-9-]/g, '');
      }

      normalizeZipValue(value) {
        const digits = String(value).replace(/\D/g, '').slice(0, 9);

        if (digits.length <= 5) return digits;

        return `${digits.slice(0, 5)}-${digits.slice(5)}`;
      }

      getZip5(value) {
        const digits = String(value).replace(/\D/g, '');

        if (digits.length < 5) return null;

        return digits.slice(0, 5).padStart(5, '0');
      }

      hasValidZipFormat(value) {
        return /^\d{5}(-\d{4})?$/.test(this.normalizeZipValue(value));
      }

      getZipValidationError(value) {
        if (!value?.trim()) return null;

        if (!this.hasValidZipFormat(value)) {
          return this.getZipFieldErrorMessage();
        }

        const zip5 = this.getZip5(value);

        if (!zip5) {
          return this.getZipFieldErrorMessage();
        }

        const state = this.getSelectedStateCode();

        if (!state) {
          return this.getZipStateRequiredMessage();
        }

        if (this.isBlockedState(state)) {
          return this.getBlockedStateMessage(state);
        }

        if (!this.isZipLibraryAvailable()) {
          this.warnZipLibraryOnce();
          return null;
        }

        const derivedState = this.getZipStateFromLibrary(zip5);

        if (!derivedState) {
          return this.getZipNotFoundMessage();
        }

        if (this.isBlockedState(derivedState)) {
          return this.getBlockedStateMessage(derivedState);
        }

        if (derivedState !== state) {
          return this.getZipStateMismatchMessage();
        }

        return null;
      }

      isValidZip(value) {
        if (!value?.trim()) return false;

        return !this.getZipValidationError(value);
      }

      validateStateField({ silent = false } = {}) {
        const field = this.stateSelect || this.form?.querySelector('#FinanceState');

        if (!field) return true;

        if (!field.value) {
          if (!silent) {
            this.setFieldError(field, this.getEmptyFieldMessage(field));
          } else {
            this.clearFieldError(field);
          }
          return false;
        }

        if (this.isBlockedState(field.value)) {
          const message = this.getBlockedStateMessage(field.value);
          this.setFieldError(field, message);
          if (!silent) {
            this.showFinanceNotification(message);
          }
          return false;
        }

        this.clearFieldError(field);
        return true;
      }

      applyZipValue(field, rawValue) {
        const sanitized = this.sanitizeZipValue(rawValue);
        const hadInvalidCharacters = sanitized !== rawValue;

        field.value = this.normalizeZipValue(sanitized);

        return { hadInvalidCharacters };
      }

      handleZipInput(field) {
        this.applyZipValue(field, field.value);
        this.clearFieldError(field);
        this.updateContinueState();
      }

      handleZipPaste(field, event) {
        event.preventDefault();

        const pastedText = event.clipboardData?.getData('text') || '';
        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? field.value.length;
        const merged = `${field.value.slice(0, start)}${pastedText}${field.value.slice(end)}`;
        this.applyZipValue(field, merged);
        this.clearFieldError(field);
        this.updateContinueState();
      }

      validateZipField(field) {
        this.applyZipValue(field, field.value);

        if (!field.value.trim()) {
          this.setFieldError(field, this.getEmptyFieldMessage(field));
          return false;
        }

        const error = this.getZipValidationError(field.value);

        if (error) {
          this.setFieldError(field, error);
          return false;
        }

        this.clearFieldError(field);
        return true;
      }

      handleNameInput(field) {
        if (this.containsPeriod(field.value)) {
          field.value = field.value.replace(/\./g, '');
        }

        this.clearFieldError(field);
        this.updateContinueState();
      }

      handleNamePaste(field, event) {
        const pastedText = event.clipboardData?.getData('text') || '';

        if (!this.containsPeriod(pastedText)) return;

        event.preventDefault();

        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? field.value.length;
        const nextValue = `${field.value.slice(0, start)}${pastedText.replace(/\./g, '')}${field.value.slice(end)}`;

        field.value = nextValue;
        this.clearFieldError(field);
        this.updateContinueState();
      }

      validateNameField(field) {
        if (!field.value.trim()) {
          this.setFieldError(field, this.getEmptyFieldMessage(field));
          return false;
        }

        if (this.containsPeriod(field.value)) {
          field.value = field.value.replace(/\./g, '');
          this.setFieldError(field, this.getNameFieldErrorMessage());
          return false;
        }

        this.clearFieldError(field);
        return true;
      }

      getFieldErrorElement(field) {
        return field.closest('.finance-field')?.querySelector('[data-finance-field-error]') || null;
      }

      setFieldError(field, message) {
        const fieldWrap = field.closest('.finance-field');

        field.setCustomValidity(message);
        field.setAttribute('aria-invalid', 'true');
        fieldWrap?.classList.add('finance-field--invalid');

        const errorEl = this.getFieldErrorElement(field);
        if (errorEl) errorEl.textContent = message;
      }

      clearFieldError(field) {
        const fieldWrap = field.closest('.finance-field');

        field.setCustomValidity('');
        field.removeAttribute('aria-invalid');
        fieldWrap?.classList.remove('finance-field--invalid');

        const errorEl = this.getFieldErrorElement(field);
        if (errorEl) errorEl.textContent = '';
        this.syncFinanceNotification();
      }

      clearAllFieldErrors() {
        this.requiredFields?.forEach((field) => this.clearFieldError(field));
      }

      showFirstFieldError() {
        const invalidField = this.form?.querySelector(
          '.finance-field--invalid input, .finance-field--invalid select, .finance-field--invalid textarea'
        );

        const message =
          invalidField?.validationMessage ||
          this.dataset.financeErrorFields ||
          'Please fix the highlighted fields.';
        this.showFinanceNotification(message);

        invalidField?.closest('.finance-field')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      getActiveFinanceNotification() {
        const step = this.querySelector(`[data-finance-step="${this._currentStep}"]`);
        return step?.querySelector(this.selectors.financeNotification) || null;
      }

      showFinanceNotification(message, items) {
        const notification = this.getActiveFinanceNotification();
        const messageEl = notification?.querySelector(this.selectors.financeNotificationMessage);

        if (!notification || !messageEl || !message) return;

        if (items?.length) {
          messageEl.innerHTML = this.formatFinanceErrorHtml(message, items);
        } else {
          messageEl.textContent = message;
        }

        notification.removeAttribute('hidden');
        requestAnimationFrame(() => {
          notification.classList.add('is-visible');
        });
      }

      clearFinanceNotification() {
        this.querySelectorAll(this.selectors.financeNotification).forEach((notification) => {
          notification.classList.remove('is-visible');
          notification.setAttribute('hidden', '');
          notification.querySelector(this.selectors.financeNotificationMessage).textContent = '';
        });
      }

      showFinanceError(message, items) {
        this.showFinanceNotification(message, items);
      }

      showShippingUnavailableMessage() {
        if (!this.shippingUnavailableMessage) return;

        this.showFinanceError(this.shippingUnavailableMessage, this.unavailableItems);
      }

      hideShippingUnavailableMessage() {
        this.clearFinanceError();
      }

      clearFinanceError() {
        this.clearFinanceNotification();
      }

      createFinanceError(message, items = []) {
        const error = new Error(message);
        error.items = Array.isArray(items) ? items : [];
        return error;
      }

      formatFinanceErrorHtml(message, items = []) {
        let html = this.escapeHtml(message);
        const titles = items
          .map((item) => item?.title)
          .filter(Boolean)
          .sort((a, b) => b.length - a.length);

        titles.forEach((title) => {
          const escapedTitle = this.escapeHtml(title);
          html = html.split(escapedTitle).join('<u>' + escapedTitle + '</u>');
        });

        return html;
      }

      setContinueLoading(isLoading) {
        if (!this.continueButton) return;

        this.continueButton.classList.toggle('loading', isLoading);
        this.continueButton
          .querySelector(this.selectors.loadingSpinner)
          ?.classList.toggle('hidden', !isLoading);
        this.updateContinueState();
      }

      setCheckoutLoading(isLoading) {
        if (!this.checkoutButton) return;

        this.checkoutButton.classList.toggle('loading', isLoading);
        this.updateCheckoutState();
        this.checkoutButton
          .querySelector(this.selectors.loadingSpinner)
          ?.classList.toggle('hidden', !isLoading);
      }

      updateCheckoutState() {
        if (!this.checkoutButton) return;

        const isLoading = this.checkoutButton.classList.contains('loading');
        const blocked =
          isLoading ||
          !this._quoteDataReady ||
          !this.shippingRates.length ||
          this.unavailableItems.length > 0 ||
          !this.orderContext.items?.length;

        this.checkoutButton.disabled = blocked;
      }

      buildRequestPayload() {
        const items = (this.orderContext.items || [])
          .filter((item) => item.variantId)
          .map((item) => ({
            variantId: String(item.variantId),
            quantity: Number(item.quantity) || 1,
          }));

        const address = {
          addressStreet1: this.form.address1.value.trim(),
          addressStreet2: this.form.address2.value.trim(),
          addressCity: this.form.city.value.trim(),
          addressState: this.form.state.value,
          addressZipCode: this.normalizeZipValue(this.form.zip.value),
          addressCountry: this.form.country.value,
        };

        return { items, address };
      }

      getQuotePayloadKey() {
        return JSON.stringify(this.buildRequestPayload());
      }

      hasCachedQuote() {
        return this._quoteDataReady && this._cachedQuotePayloadKey === this.getQuotePayloadKey();
      }

      buildContactPayload() {
        return {
          firstName: this.form.first_name.value.trim(),
          lastName: this.form.last_name.value.trim(),
          email: this.form.email.value.trim(),
          mobilePhoneNumber: this.normalizePhoneDigits(this.form.phone.value),
        };
      }

      getSelectedShippingRate() {
        return (
          this.shippingRates.find((rate) => rate.handle === this.selectedShippingHandle) || null
        );
      }

      assertValidQuoteResponse(data) {
        if (data?.error_message) {
          throw this.createFinanceError(String(data.error_message), data.items);
        }

        const shippingRates = data?.shippingRates;
        if (!Array.isArray(shippingRates) || shippingRates.length === 0) {
          throw this.createFinanceError(this.message('shippingUnavailable', 'Shipping is unavailable for this address.'));
        }
      }

      parseResponseJson(text, status = 0) {
        if (!text) {
          throw new Error(`Finance request failed (${status})`);
        }

        try {
          return JSON.parse(text);
        } catch {
          throw new Error(`Finance request returned invalid JSON (${status})`);
        }
      }

      isShippingUnavailableResponse(parsed, { ok } = {}) {
        if (!parsed || typeof parsed !== 'object') return false;

        if (parsed.error === 'shipping_unavailable') return true;

        const items = Array.isArray(parsed.items) ? parsed.items : [];
        if (!items.length) return false;

        if (parsed.error_message) return true;
        if (!ok && (parsed.message || parsed.error_message)) return true;

        return false;
      }

      isShippingUnavailableError(error) {
        return Boolean(error?.items?.length);
      }

      applyShippingUnavailableState(parsed) {
        this.unavailableItems = (parsed.items || []).map((item) => ({
          variantId: String(item.variantId || item.variant_id || ''),
          title: item.title || '',
        })).filter((item) => item.variantId);
        this.shippingUnavailableMessage =
          parsed.message ||
          parsed.error_message ||
          this.message('productsUnavailable', 'Some products are unavailable for delivery to your location.');
        this.shippingRates = [];
        this.selectedShippingHandle = null;
        this._quoteDataReady = false;
        this._cachedQuotePayloadKey = null;
        this.pricing = { subtotal: 0, shipping: 0, tax: 0, total: 0 };
        this.toggleShippingSection(false);
        this.renderSummaryProducts(this.orderContext.items);
        this.resetSummaryDisplay();
        this.updateCheckoutState();
        requestAnimationFrame(() => this.updateScrollFades());
      }

      requestQuote() {
        const payload = this.buildRequestPayload();
        const body = JSON.stringify(payload);

        this.logFinance('Quote request — sending:', payload);

        return this.fetchFinanceQuote(body).then((response) => {
          this.logFinance('Quote response — raw:', {
            ok: response.ok,
            status: response.status,
            text: response.text,
          });

          const parsed = this.parseResponseJson(response.text, response.status);

          if (this.isShippingUnavailableResponse(parsed, response)) {
            this.logFinance('Quote response — shipping unavailable:', parsed);
            this.applyShippingUnavailableState(parsed);
            return { status: 'unavailable' };
          }

          const data = this.getFinanceResponseData(response, parsed);
          this.logFinance('Quote response — parsed:', data);
          this.assertValidQuoteResponse(data);
          this.unavailableItems = [];
          this.shippingUnavailableMessage = '';
          this.hideShippingUnavailableMessage();
          this.setCheckoutUrl(routes.finance_checkout_url);
          this.applyStep2Data(this.normalizeRatesResponse(data));
          this._cachedQuotePayloadKey = this.getQuotePayloadKey();
          this._quoteDataReady = true;
          this.updateCheckoutState();
          return { status: 'success' };
        });
      }

      removeOrderItem(variantId) {
        const normalizedId = String(variantId);

        this.orderContext.items = (this.orderContext.items || []).filter(
          (item) => String(item.variantId) !== normalizedId
        );
        this.unavailableItems = this.unavailableItems.filter(
          (item) => String(item.variantId) !== normalizedId
        );
        this._quoteDataReady = false;
        this._cachedQuotePayloadKey = null;
        this.shippingRates = [];
        this.selectedShippingHandle = null;
        this.pricing = { subtotal: 0, shipping: 0, tax: 0, total: 0 };
        this.toggleShippingSection(false);
        this.resetSummaryDisplay();

        if (!this.orderContext.items.length) {
          this.renderSummaryProducts([]);
          this.showFinanceError(this.message('emptyOrder', 'Your order is empty. Close this window and select a product to continue.'));
          this.updateCheckoutState();
          return;
        }

        this.renderSummaryProducts(this.orderContext.items);

        if (this.unavailableItems.length) {
          this.showShippingUnavailableMessage();
          this.updateCheckoutState();
          requestAnimationFrame(() => this.updateScrollFades());
          return;
        }

        this.setCheckoutLoading(true);
        this.requestQuote()
          .then((result) => {
            if (result?.status === 'unavailable') {
              this.showShippingUnavailableMessage();
            } else {
              this.clearFinanceError();
            }
          })
          .catch((error) => {
            this.logFinance('Quote refresh — error:', error.message);

            if (this._isClosing || !this.isOpen()) return;

            if (this.isShippingUnavailableError(error)) {
              this.applyShippingUnavailableState({
                message: error.message,
                items: error.items,
              });
              this.showShippingUnavailableMessage();
              return;
            }

            this.showFinanceError(this.getFriendlyRequestError(error), error.items);
          })
          .finally(() => {
            this.setCheckoutLoading(false);
            requestAnimationFrame(() => this.updateScrollFades());
          });
      }

      buildShippingPayload() {
        const selectedRate = this.getSelectedShippingRate();

        if (!selectedRate) {
          throw new Error(this.message('selectShipping', 'Please select a shipping method.'));
        }

        return {
          handle: selectedRate.handle,
          title: selectedRate.title,
          price: selectedRate.price / 100,
        };
      }

      buildAttributionPayload() {
        const rs = window.RS || {};
        // Click ids are optional. A cookie failure must not abort checkout.
        const cookie = (name) => {
          try {
            if (typeof rs.cookie !== 'function') return '';
            const value = rs.cookie(name);
            return typeof value === 'string' ? value : '';
          } catch (error) {
            return '';
          }
        };

        return {
          landingSite: typeof rs.landingSite === 'function' ? rs.landingSite() : '',
          visitorId: typeof rs.visitorId === 'function' ? rs.visitorId() : '',
          oppref: cookie('__oppref'),
          obref: cookie('__obref'),
        };
      }

      buildMarketingConsentPayload() {
        return {
          email: document.getElementById('rs-email-optin')?.checked === true,
          sms: document.getElementById('rs-sms-optin')?.checked === true,
        };
      }

      buildCheckoutPayload() {
        const { items, address } = this.buildRequestPayload();
        const payload = {
          contact: this.buildContactPayload(),
          items,
          address,
          delivery: 'redirect',
          shipping: this.buildShippingPayload(),
          attribution: this.buildAttributionPayload(),
          marketingConsent: this.buildMarketingConsentPayload(),
        };

        const returnToMerchantUrl = this.getReturnToMerchantUrl();
        if (returnToMerchantUrl) {
          payload.returnToMerchantUrl = returnToMerchantUrl;
        }

        return payload;
      }

      getReturnToMerchantUrl() {
        const items = this.orderContext?.items || [];
        const candidates =
          this.orderContext?.type === 'product'
            ? items.slice(0, 1)
            : items.filter((item) => Number(item.financePrice) > 0);

        for (const item of candidates) {
          const url = this.normalizeMerchantProductUrl(item?.url);
          if (url) return url;
        }

        return '';
      }

      normalizeMerchantProductUrl(value) {
        if (typeof value !== 'string' || !value) return '';

        let url;
        try {
          url = new URL(value);
        } catch {
          return '';
        }

        if (url.protocol !== 'https:') return '';
        if (!url.pathname.includes('/products/')) return '';
        if (/thank[-_]you/i.test(url.pathname)) return '';

        url.search = '';
        url.hash = '';
        return url.toString();
      }

      abortFinanceRequest() {
        if (this._financeTimeoutId) {
          window.clearTimeout(this._financeTimeoutId);
          this._financeTimeoutId = null;
        }

        if (this._financeAbortController) {
          this._financeAbortController.abort();
          this._financeAbortController = null;
        }
      }

      fetchWithTimeout(url, options = {}, timeoutMs = this.financeRequestTimeoutMs) {
        this.abortFinanceRequest();

        const controller = new AbortController();
        this._financeAbortController = controller;
        this._financeTimeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

        return fetch(url, { ...options, signal: controller.signal }).finally(() => {
          if (this._financeTimeoutId) {
            window.clearTimeout(this._financeTimeoutId);
            this._financeTimeoutId = null;
          }

          if (this._financeAbortController === controller) {
            this._financeAbortController = null;
          }
        });
      }

      getFriendlyRequestError(error) {
        if (error?.name === 'AbortError') {
          return this.dataset.financeErrorTimeout || 'The request timed out. Please try again.';
        }

        const message = String(error?.message || '');
        if (
          error instanceof TypeError ||
          /failed to fetch|networkerror|load failed|network request failed/i.test(message)
        ) {
          return this.dataset.financeErrorNetwork || 'Could not reach Eve. Check your connection and try again.';
        }

        return message || this.dataset.financeErrorGeneric || 'Something went wrong. Please try again.';
      }

      applyApiFieldErrors(fieldErrors) {
        if (!fieldErrors || typeof fieldErrors !== 'object') return;

        const fieldMap = {
          firstName: '#FinanceFirstName',
          first_name: '#FinanceFirstName',
          lastName: '#FinanceLastName',
          last_name: '#FinanceLastName',
          email: '#FinanceEmail',
          phone: '#FinancePhone',
          mobilePhoneNumber: '#FinancePhone',
          address1: '#FinanceAddress1',
          addressStreet1: '#FinanceAddress1',
          city: '#FinanceCity',
          addressCity: '#FinanceCity',
          state: '#FinanceState',
          addressState: '#FinanceState',
          zip: '#FinanceZip',
          addressZipCode: '#FinanceZip',
        };

        Object.entries(fieldErrors).forEach(([key, messages]) => {
          const selector = fieldMap[key];
          const field = selector ? this.form?.querySelector(selector) : null;
          if (!field) return;

          const text = Array.isArray(messages) ? messages.join(', ') : String(messages);
          if (text) this.setFieldError(field, text);
        });
      }

      fetchFinanceQuote(body) {
        return this.fetchWithTimeout(`${routes.finance_quote_url}`, { ...fetchConfig(), body }).then((response) =>
          this.parseFinanceResponse(response)
        );
      }

      getCheckoutUrl() {
        return this.parseJsonElement(this.checkoutUrlScript)?.url || '';
      }

      setCheckoutUrl(url) {
        this.writeJsonScript(this.checkoutUrlScript, { url: url || '' });
      }

      fetchFinanceCheckout(body, url) {
        return this.fetchWithTimeout(url, { ...fetchConfig(), body }).then((response) =>
          this.parseFinanceResponse(response)
        );
      }

      toCents(amount) {
        return Math.round(Number(amount) * 100);
      }

      normalizeRatesResponse(response) {
        const countryCode = this.form?.country?.value || 'US';
        const country = this.getCountryConfig(countryCode);
        const financing = response.financing;
        const useFinancing = Boolean(financing?.applied);

        this.currency = response.currency || country?.currency || 'USD';
        this.locale = country?.locale || 'en-US';

        return {
          useFinancing,
          subtotal: this.toCents(useFinancing ? financing.subtotal : response.subtotal),
          tax: this.toCents(useFinancing ? financing.tax : response.tax),
          total: this.toCents(useFinancing ? financing.total : response.total),
          shippingRates: (response.shippingRates || []).map((rate) => ({
            handle: rate.handle,
            title: rate.title,
            price: this.toCents(rate.price),
            tax: this.toCents(useFinancing ? rate.financingTax ?? rate.tax : rate.tax),
            total: this.toCents(useFinancing ? rate.financingTotal ?? rate.total : rate.total),
          })),
        };
      }

      parseFinanceResponse(response) {
        return response.text().then((text) => ({
          ok: response.ok,
          status: response.status,
          text,
        }));
      }

      getFinanceResponseData({ ok, status, text }, parsed = null) {
        const data = parsed ?? this.parseResponseJson(text, status);

        if (!ok) {
          const fieldErrors = data.details?.fieldErrors || data.fieldErrors || {};
          const details = Object.keys(fieldErrors).length
            ? Object.entries(fieldErrors)
                .map(([field, messages]) => {
                  const text = Array.isArray(messages) ? messages.join(', ') : String(messages);
                  return `${field}: ${text}`;
                })
                .join('; ')
            : '';
          const message =
            data.message ||
            data.error_message ||
            (typeof data.error === 'string' ? data.error : '') ||
            `Finance request failed (${status})`;
          const fullMessage = details ? `${message} — ${details}` : message;
          this.applyApiFieldErrors(fieldErrors);
          throw this.createFinanceError(fullMessage, data.items);
        }

        return data;
      }

      onBackClick() {
        if (this.checkoutButton?.classList.contains('loading')) return;

        if (this._currentStep === 2) {
          this.clearFinanceNotification();
          this.setCheckoutLoading(false);
          this.unavailableItems = [];
          this.shippingUnavailableMessage = '';
          this.hideShippingUnavailableMessage();
          this._quoteDataReady = false;
          this._cachedQuotePayloadKey = null;
          this.showStep(1);
          return;
        }

        if (this._currentStep === 1 && this.shouldShowEveOption() && this.shouldShowShopPayOption()) {
          this.clearFinanceError();
          this.hideShippingUnavailableMessage();
          this._quoteDataReady = false;
          this._cachedQuotePayloadKey = null;
          this.showStep(0);
        }
      }

      onContinueClick() {
        if (this.continueButton.classList.contains('loading')) return;

        if (!this.requireTermsAccepted('eve')) return;

        if (!this.validateStep1Form()) {
          return;
        }

        this.handleContinue();
      }

      handleContinue() {
        this.clearFinanceError();

        const blockedAddressError = this.getBlockedAddressError();
        if (blockedAddressError) {
          this.validateStateField();
          this.zipFields.forEach((field) => this.validateZipField(field));
          this.showFinanceNotification(blockedAddressError);
          return;
        }

        if (this.hasCachedQuote()) {
          this.logFinance('Quote request — skipped (form unchanged)');
          this.showStep(2);
          this.updateCheckoutState();
          return;
        }

        this.setContinueLoading(true);

        this.requestQuote()
          .then((result) => {
            this.showStep(2);
            if (result?.status === 'unavailable') {
              this.showShippingUnavailableMessage();
            } else {
              this.clearFinanceError();
            }
          })
          .catch((error) => {
            this.logFinance('Quote request — error:', error.message);

            if (this._isClosing || !this.isOpen()) return;

            if (this.isShippingUnavailableError(error)) {
              this.applyShippingUnavailableState({
                message: error.message,
                items: error.items,
              });
              this.showStep(2);
              this.showShippingUnavailableMessage();
              return;
            }

            this.showFinanceError(this.getFriendlyRequestError(error), error.items);
          })
          .finally(() => {
            this.setContinueLoading(false);
          });
      }

      applyStep2Data(response) {
        this.shippingRates = response.shippingRates;
        this.selectedShippingHandle = this.shippingRates[0]?.handle || null;
        this._apiQuoteSubtotal = response.subtotal;
        this.pricing.subtotal = response.subtotal;
        this.applySelectedRate(this.shippingRates[0]);

        this.toggleShippingSection(true);
        this.renderSummaryProducts(this.orderContext.items);
        this.renderShippingOptions();
        this.updateSummaryDisplay();
        this.updateCheckoutState();
        requestAnimationFrame(() => this.updateScrollFades());
      }

      toggleShippingSection(showShippingSection) {
        const hidden = this.selectors.attributes.hidden;

        this.shippingSection?.toggleAttribute(hidden, !showShippingSection);
        this.shippingDivider?.toggleAttribute(hidden, !showShippingSection);
      }

      applySelectedRate(rate) {
        if (!rate) return;

        this.selectedShippingHandle = rate.handle;
        this.pricing.shipping = rate.price;
        this.pricing.tax = rate.tax;
        this.pricing.total = rate.total;
        this.applyCartSummaryTotals();
      }

      getCartEveSubtotal() {
        if (this.orderContext?.type !== 'cart') return null;

        const items = this.orderContext.items || [];
        const base = items.reduce((sum, item) => sum + this.getItemDisplayPrice(item), 0);
        const orderDiscount = items.reduce((sum, item) => sum + this.getItemOrderDiscount(item), 0);

        return Math.max(0, base - orderDiscount);
      }

      applyCartSummaryTotals() {
        const eveSubtotal = this.getCartEveSubtotal();
        if (!Number.isFinite(eveSubtotal)) return;

        const apiSubtotal = Number(this._apiQuoteSubtotal);
        const apiTax = Number(this.pricing.tax) || 0;
        this.pricing.subtotal = eveSubtotal;

        if (Number.isFinite(apiSubtotal) && apiSubtotal > 0 && apiSubtotal !== eveSubtotal) {
          this.pricing.tax = Math.round((apiTax * eveSubtotal) / apiSubtotal);
        }

        this.pricing.total =
          this.pricing.subtotal + (Number(this.pricing.shipping) || 0) + (Number(this.pricing.tax) || 0);
      }

      getShippingSubtitle(title) {
        if (!title || !this.shippingSubtitles) return '';

        if (this.shippingSubtitles[title]) return this.shippingSubtitles[title];

        const normalizedTitle = title.trim().toLowerCase();
        const entries = Object.entries(this.shippingSubtitles);

        const caseInsensitiveMatch = entries.find(
          ([key]) => key.trim().toLowerCase() === normalizedTitle
        );
        if (caseInsensitiveMatch) return caseInsensitiveMatch[1];

        const partialMatch = entries.find(([key]) => {
          const normalizedKey = key.trim().toLowerCase();
          return normalizedTitle.includes(normalizedKey) || normalizedKey.includes(normalizedTitle);
        });

        return partialMatch ? partialMatch[1] : '';
      }

      renderShippingOptions() {
        if (!this.shippingOptionsContainer) return;

        const s = this.selectors;
        const shippingOptionAttr = s.attributes.shippingOption;

        this.shippingOptionsContainer.innerHTML = '';

        this.shippingRates.forEach((rate, index) => {
          const button = document.createElement('button');
          const subtitle = this.getShippingSubtitle(rate.title);
          const isSelected = rate.handle === this.selectedShippingHandle;
          const isStaticFreeShipping =
            rate.handle === 'free-shipping' && this.shippingRates.length === 1 && rate.price === 0;

          button.type = 'button';
          button.className =
            'finance-shipping-option' +
            (isSelected ? ' is-selected' : '') +
            (isStaticFreeShipping ? ' finance-shipping-option--static' : '');
          button.setAttribute(shippingOptionAttr, rate.handle);

          if (isStaticFreeShipping) {
            button.disabled = true;
          }

          button.innerHTML =
            '<span class="finance-shipping-option__left">' +
            '<span class="finance-shipping-option__radio" aria-hidden="true"></span>' +
            '<span class="finance-shipping-option__details">' +
            '<p class="finance-shipping-option__name">' +
            this.escapeHtml(rate.title) +
            '</p>' +
            (subtitle
              ? '<p class="finance-shipping-option__time">' + this.escapeHtml(subtitle) + '</p>'
              : '') +
            '</span>' +
            '</span>' +
            '<span class="finance-shipping-option__price">' +
            this.formatMoney(rate.price) +
            '</span>';

          button.addEventListener('click', () => {
            if (isStaticFreeShipping) return;

            this.applySelectedRate(rate);

            this.shippingOptionsContainer.querySelectorAll(s.shippingOption).forEach((el) => {
              el.classList.toggle(
                'is-selected',
                el.getAttribute(shippingOptionAttr) === rate.handle
              );
            });

            this.updateSummaryDisplay();
          });

          this.shippingOptionsContainer.appendChild(button);
        });
      }

      onCheckoutClick() {
        if (
          this.checkoutButton.disabled ||
          this.checkoutButton.classList.contains('loading') ||
          !this.getCheckoutUrl()
        ) {
          return;
        }

        if (this.unavailableItems.length) {
          this.showShippingUnavailableMessage();
          return;
        }

        if (!this._quoteDataReady || !this.shippingRates.length) {
          return;
        }

        this.handleCheckout();
      }

      handleCheckout() {
        const checkoutUrl = this.getCheckoutUrl();
        if (!checkoutUrl) return;

        let payload;

        try {
          payload = this.buildCheckoutPayload();
        } catch (error) {
          this.showFinanceError(
            error.message || this.message('generic', 'Something went wrong. Please try again.'),
            error.items
          );
          return;
        }

        this.clearFinanceError();
        this.setCheckoutLoading(true);
        this.showStep(3);

        const body = JSON.stringify(payload);

        this.logFinance('Checkout request — sending:', payload);

        this.fetchFinanceCheckout(body, checkoutUrl)
          .then((response) => {
            this.logFinance('Checkout response — raw:', {
              ok: response.ok,
              status: response.status,
              text: response.text,
            });

            const data = this.getFinanceResponseData(response);
            this.logFinance('Checkout response — parsed:', data);

            if (!data.eveUrl) {
              throw new Error(this.message('eveUrl', 'Eve did not return a checkout link. Please try again.'));
            }

            window.location.href = data.eveUrl;
          })
          .catch((error) => {
            this.logFinance('Checkout request — error:', error.message);
            this.setCheckoutLoading(false);

            if (this._isClosing || !this.isOpen()) return;

            this.showStep(2);

            if (error.items?.length) {
              this.applyShippingUnavailableState({
                message: error.message,
                items: error.items,
              });
              this.showShippingUnavailableMessage();
              return;
            }

            this.showFinanceError(this.getFriendlyRequestError(error), error.items);
          })
          .finally(() => {
            this.setCheckoutLoading(false);
          });
      }

      renderSummaryProducts(items) {
        if (!this.summaryProductsContainer) return;

        this.summaryProductsContainer.innerHTML = '';

        if (!items || items.length === 0) return;

        const unavailableIds = new Set(
          this.unavailableItems.map((item) => String(item.variantId))
        );

        items.forEach((item) => {
          const productEl = document.createElement('div');
          const variantId = String(item.variantId || '');
          const isUnavailable = unavailableIds.has(variantId);

          productEl.className =
            'finance-summary__product' +
            (isUnavailable ? ' finance-summary__product--unavailable' : '');

          const tags = (item.tags || []).slice();
          tags.push('Qty: ' + (item.quantity || 1));

          const tagsHtml = tags
            .map((tag) => '<span class="finance-summary__tag">' + this.escapeHtml(tag) + '</span>')
            .join('');

          const imageHtml = item.image
            ? '<img class="finance-summary__image" src="' +
              this.escapeHtml(item.image) +
              '" alt="' +
              this.escapeHtml(item.title || '') +
              '" width="48" height="48" loading="lazy">'
             : '';

          const removeButtonHtml = isUnavailable
            ? '<button type="button" class="button button--tertiary finance-summary__remove" data-finance-remove-item="' +
              this.escapeHtml(variantId) +
              '" aria-label="' +
              this.escapeHtml(this.removeItemLabel + ' ' + (item.title || '')) +
              '">' +
              this.removeIconHtml +
              '</button>'
            : '';

          const unavailableNoteHtml = isUnavailable
            ? '<p class="finance-summary__unavailable-note">' +
              this.escapeHtml(this.unavailableItemLabel) +
              '</p>'
            : '';

          const asideHtml =
            '<div class="finance-summary__aside">' +
            '<p class="finance-summary__product-price">' +
            this.formatMoney(this.getItemDisplayPrice(item)) +
            '</p>' +
            removeButtonHtml +
            '</div>';

          productEl.innerHTML =
            imageHtml +
            '<div class="finance-summary__main">' +
            '<p class="finance-summary__product-title">' +
            this.escapeHtml(item.title || '') +
            '</p>' +
            unavailableNoteHtml +
            '<div class="finance-summary__tags">' +
            tagsHtml +
            '</div>' +
            '</div>' +
            asideHtml;

          this.summaryProductsContainer.appendChild(productEl);
        });
      }

      resetSummaryDisplay() {
        if (this.summarySubtotal) this.summarySubtotal.textContent = '—';
        if (this.summaryShipping) this.summaryShipping.textContent = '—';
        if (this.summaryTax) this.summaryTax.textContent = '—';
        if (this.summaryTotal) this.summaryTotal.textContent = '—';
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      updateSummaryDisplay() {
        if (this.summarySubtotal) {
          this.summarySubtotal.textContent = this.formatMoney(this.pricing.subtotal);
        }
        if (this.summaryShipping) {
          this.summaryShipping.textContent = this.formatMoney(this.pricing.shipping);
        }
        if (this.summaryTax) {
          this.summaryTax.textContent = this.formatMoney(this.pricing.tax);
        }
        if (this.summaryTotal) {
          this.summaryTotal.textContent = this.formatMoney(this.pricing.total);
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      getItemOrderDiscount(item) {
        const orderDiscount = Number(item?.orderDiscount) || 0;
        return Number.isFinite(orderDiscount) && orderDiscount > 0 ? orderDiscount : 0;
      }

      getItemDisplayPrice(item) {
        // financePrice is whole dollars (e.g. 700 = $700). Other cart lines use the
        // discounted line total so the Subtotal matches the cart sale price.
        const unitFinanceDollars = Number(item?.financePrice);
        const quantity = Math.max(1, parseInt(item?.quantity || 1, 10) || 1);

        if (Number.isFinite(unitFinanceDollars) && unitFinanceDollars > 0) {
          return unitFinanceDollars * 100 * quantity;
        }

        return Number(item?.price) || 0;
      }

      getItemBnplPrice(item) {
        const price = Number(item?.price) || 0;
        // Cart line price is already the line total; product price is per unit.
        if (this.orderContext?.type === 'cart') return price;

        const quantity = Math.max(1, parseInt(item?.quantity || 1, 10) || 1);
        return price * quantity;
      }

      updateMethodCardPrices() {
        const items = this.orderContext?.items || [];
        let upfrontCents = 0;
        let eveCents = 0;

        items.forEach((item) => {
          upfrontCents += this.getItemBnplPrice(item);
          eveCents += this.getItemDisplayPrice(item);
        });

        const cartEveSubtotal = this.getCartEveSubtotal();
        if (Number.isFinite(cartEveSubtotal)) eveCents = cartEveSubtotal;

        const cartTotal = this.normalizeCartTotal(this.orderContext?.cartTotal);
        if (this.orderContext?.type === 'cart' && cartTotal !== null) {
          upfrontCents = cartTotal;
        }

        this.setMethodCardPrice(this.methodPriceTraditional, eveCents);
        this.setMethodText(this.methodPriceBnpl, upfrontCents);
        this.setMethodText(this.methodUpfrontButtonPrice, upfrontCents);

        const savingsCents = eveCents - upfrontCents;
        const hasSavings = Number.isFinite(savingsCents) && savingsCents > 0;
        this.toggleHidden(this.upfrontTitleSavings, !hasSavings);
        this.toggleHidden(this.upfrontTitlePlain, hasSavings);
        if (hasSavings) {
          this.setMethodText(this.methodSavings, savingsCents);
        }
      }

      setMethodText(el, cents) {
        if (!el) return;

        if (!Number.isFinite(cents) || cents < 0) {
          el.textContent = '';
          return;
        }

        el.textContent = this.formatMoney(cents);
      }

      toggleHidden(el, hidden) {
        if (!el) return;
        el.toggleAttribute('hidden', hidden);
      }

      setMethodCardPrice(el, cents) {
        if (!el) return;

        if (!Number.isFinite(cents) || cents <= 0) {
          el.textContent = '';
          el.setAttribute('hidden', '');
          return;
        }

        el.textContent = this.formatMoney(cents);
        el.removeAttribute('hidden');
      }
    }
  );
}
